import { Table } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CustomPopup, CustomTable, Filter } from "../../../components";
import { moneyFormat } from "../../../plugins/numberFormat";
import { FinancialDetailReport } from "./components";
import { filterKeyFinancialHeader } from "./enumeration/filterKeyEnum";
import { filterSetupFinancialHeader } from "./enumeration/filterSetupEnum";
import { useGetFinancialReport } from "./hooks/financial.hooks";
import styles from "./style.module.scss";
import "./style.scss";

const FinancialReport = () => {
  const { t } = useTranslation();
  const [localData, setLocalData] = useState([]);
  const [openDetailDrawer, setOpenDetailDrawer] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [summary, setSummary] = useState({});

  const [filterDefaultValue, setFilterDefaultValue] = useState({
    [filterKeyFinancialHeader.START_DATE]: dayjs().subtract(1, "months").format("YYYY-MM-DD"),
    [filterKeyFinancialHeader.END_DATE]: dayjs().format("YYYY-MM-DD"),
  });

  const [filterValue, setFilterValue] = useState({
    [filterKeyFinancialHeader.START_DATE]: dayjs().subtract(1, "months").format("YYYY-MM-DD"),
    [filterKeyFinancialHeader.END_DATE]: dayjs().format("YYYY-MM-DD"),
  });

  const { data, hasNextPage, fetchNextPage, isLoading, isError } = useGetFinancialReport({
    params: {
      ...filterValue,
    },
    options: {
      getNextPageParam: (lastPage, pages) => {
        if (pages.length < lastPage.last_page) {
          return pages.length + 1;
        } else {
          return undefined;
        }
      },
    },
  });

  const onHandleSearch = () => {
    console.debug("search with params", filterValue);
  };

  const onOpenDetail = (date) => {
    setSelectedDate(date);
    setOpenDetailDrawer(true);
  };

  const resetDrawer = () => {
    setSelectedDate(null);
  };

  const columns = [
    {
      title: t("normal.date"),
      dataIndex: "date",
      align: "center",
      width: 150,
      render: (text, record, index) => {
        return (
          <div onClick={() => onOpenDetail(record.date)} className={styles.date}>
            {text}
          </div>
        );
      },
    },
    {
      title: t("financialReport.depositUser"),
      align: "center",
      width: 90,
      dataIndex: "total_deposit_count",
    },
    {
      title: t("financialReport.depositTotal"),
      align: "center",
      width: 90,
      dataIndex: "total_deposit_amount",
      render: (text, record, index) => moneyFormat(text),
    },
    {
      title: t("financialReport.firstDepositUser"),
      align: "center",
      width: 120,
      dataIndex: "total_today_first_deposit_count",
    },
    {
      title: t("financialReport.firstDepositTotal"),
      dataIndex: "total_today_first_deposit_amount",
      align: "center",
      width: 120,
      render: (text, record, index) => moneyFormat(text),
    },
    {
      title: t("financialReport.withdrawUser"),
      dataIndex: "total_withdrawal_count",
      align: "center",
      width: 90,
    },
    {
      title: t("financialReport.wthdrawTotal"),
      dataIndex: "total_withdrawal_amount",
      align: "center",
      width: 90,
      render: (text, record, index) => moneyFormat(text),
    },
  ];

  const loadMore = () => {
    fetchNextPage();
  };

  useEffect(() => {
    if (data) {
      if (currentPage === 0) {
        setLocalData([...data.pages[0].lists]);
      } else {
        setLocalData((prevState) => {
          return [...prevState, ...data.pages[currentPage].lists];
        });
      }

      if (data.pages[currentPage].current_page < data.pages[currentPage].last_page) {
        setCurrentPage(currentPage);
      }
      setSummary(data.pages[0].total);
    }
  }, [data, currentPage]);

  return (
    <div className={styles.container}>
      <Filter
        customDropdownStyleClass={"financial-filter"}
        customDropDownContainerStyleClass={"financial-filter-container"}
        filterSetup={filterSetupFinancialHeader}
        filterValue={filterValue}
        filterDefaultValue={filterDefaultValue}
        setFilterValue={setFilterValue}
        onReset={() => {
          //if need some aditional reset logic in the page
          console.debug("RESET FILTER");
        }}
        onSubmitSearch={onHandleSearch}
        customDropdownTitle={{
          date: `${filterValue[filterKeyFinancialHeader.START_DATE]} ${t("normal.to")} ${
            filterValue[filterKeyFinancialHeader.END_DATE]
          }`,
        }}
      />

      {isLoading ? (
        ""
      ) : isError ? (
        ""
      ) : (
        <CustomTable
          columns={columns}
          dataSource={localData}
          hasMore={hasNextPage}
          loadMore={loadMore}
          showInifineScroll={true}
          summary={() => {
            return (
              <Table.Summary fixed="top">
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <div className={styles.summaryCell}>{t("normal.summary")}</div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {moneyFormat(summary?.total_deposit_amount || 0)}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {summary?.total_today_first_deposit_count || 0}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {moneyFormat(summary?.total_today_first_deposit_amount || 0)}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5}></Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {moneyFormat(summary?.total_withdrawal_amount || 0)}
                    </div>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      )}
      <CustomPopup
        visible={openDetailDrawer}
        onClose={() => {
          setOpenDetailDrawer(false);
        }}
        afterClose={resetDrawer}
        title={selectedDate}
        destroyOnClose={true}
        forceRender={true}
      >
        <FinancialDetailReport date={selectedDate} />
      </CustomPopup>
    </div>
  );
};

export default FinancialReport;
